import type { DataProvider } from "@refinedev/core";
import type { AxiosInstance } from "axios";

import { attachFirebaseAuth, dataProvider } from "@/providers/data-provider";

const API_VERSION = { "api-version": "1.0" };

/**
 * Data provider for `/areas` (resource `areas`).
 *
 * `getList` is the default provider's: `GET /areas/explore`, the Typesense search every other
 * entity lists through. The rest goes to the Firestore-backed routes: `getOne`, `create`,
 * `update`, and `custom`, which addresses a sub-route through `meta.urlPath`, relative to
 * `/areas` (for example `tree`, `1/aliases`, `1/sync-venues`).
 */
export const areasDataProvider = (
  apiUrl: string,
  client: AxiosInstance,
): DataProvider => {
  attachFirebaseAuth(client);
  const base = `${apiUrl}/areas`;
  const explore = dataProvider(apiUrl, client);

  return {
    getList: (params) => explore.getList(params),

    getOne: async ({ id }) => {
      const r = await client.get(`${base}/${encodeURIComponent(String(id))}`, {
        params: API_VERSION,
      });
      return { data: r.data };
    },

    create: async ({ variables }) => {
      const r = await client.post(base, variables, { params: API_VERSION });
      return { data: r.data };
    },

    update: async ({ id, variables }) => {
      const r = await client.put(
        `${base}/${encodeURIComponent(String(id))}`,
        variables,
        { params: API_VERSION },
      );
      return { data: r.data };
    },

    custom: async ({ method, payload, query, headers, meta }) => {
      const path = meta?.urlPath ? `/${meta.urlPath}` : "";
      const r = await client({
        method: method ?? "get",
        url: `${base}${path}`,
        params: { ...API_VERSION, ...query },
        data: payload,
        headers,
      });
      return { data: r.data };
    },

    getMany: async () => ({ data: [] }),
    deleteOne: async () => {
      throw new Error("Areas cannot be deleted");
    },
    getApiUrl: () => apiUrl,
  };
};
